import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, tap, withLatestFrom, catchError } from 'rxjs/operators';
import { from } from 'rxjs';
import * as fromActions from './actions';
import { Store } from '@ngrx/store';
import { LicenceService } from '@services/api/api.service';
import { selectDataTrafficLicences, selectSelectedDataTrafficLicence, selectSelectedStoreOrderLicence, selectStoreOrderLicences } from '@redux/modules/licences/index';
import { selectUser } from '@redux/index';

@Injectable()
export class LicenceEffects {
  private language = 'en';

  setDataTrafficLicencesFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setDataTrafficLicencesFilters),
    tap(() => {
      this.store.dispatch(fromActions.setDataTrafficLicencesPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  setStoreOrderLicencesFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setStoreOrderLicencesFilters),
    tap(() => {
      this.store.dispatch(fromActions.setStoreOrderLicencesPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  setSelectedDataTrafficLicenceFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setSelectedDataTrafficLicenceFilters),
    tap(() => {
      this.store.dispatch(fromActions.setSelectedDataTrafficLicencePaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  setSelectedStoreOrderLicenceFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setSelectedStoreOrderLicenceFilters),
    tap(() => {
      this.store.dispatch(fromActions.setSelectedStoreOrderLicencePaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getDataTrafficLicencesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getDataTrafficLicencesList,
        fromActions.setDataTrafficLicencesPaginator),
      withLatestFrom(
        this.store.select(selectDataTrafficLicences),
      ),
      tap(() => this.store.dispatch(fromActions.setDataTrafficLicencesLoading({ isLoading: true }))),
      switchMap(([_, dataTrafficLicences]) => {
        // Helper to apply search, checkbox and filterModel and sort to a dataset
        const applyClientFilters = (data: any[] = []) => {
          const filters = dataTrafficLicences?.filters || {};

          let result = Array.isArray(data) ? data.slice() : [];

          // qSearch: apply to vin and ctmSerialNumber
          const q = (filters.qSearch || '').toString().trim().toLowerCase();
          if (q) {
            result = result.filter(item => {
              const vin = (item.vin || '').toString().toLowerCase();
              const ctm = (item.ctmSerialNumber || '').toString().toLowerCase();
              const customerName = (item.customerName || '').toString().toLowerCase();
              return vin.includes(q) || ctm.includes(q) || customerName.includes(q);
            });
          }

          // checkboxFilters: active, expiring, expired
          const cb = filters.checkboxFilters;
          if (cb && (cb.active || cb.expiring || cb.expired)) {
            result = result.filter(item => {
              const dataTrafficLicencesArr = Array.isArray(item.licences) ? item.licences : [];
              const hasExpiring = dataTrafficLicencesArr.some(lic => lic.active && Boolean((lic as any).expiring));
              const hasActive = dataTrafficLicencesArr.some(lic => lic.active);
              const isExpired = !hasActive;

              let keep = false;
              if (cb.expiring && hasExpiring) keep = true;
              if (cb.active && hasActive) keep = true;
              if (cb.expired && isExpired) keep = true;

              return keep;
            });
          }

          // filterModel (e.g. licenceStatus equals some value)
          const filterModel = filters.filterModel;
          if (filterModel && filterModel.licenceStatus && filterModel.licenceStatus.filter) {
            const status = filterModel.licenceStatus.filter;
            result = result.filter(item => {
              const dataTrafficLicencesArr = Array.isArray(item.licences) ? item.licences : [];
              const hasExpiring = dataTrafficLicencesArr.some(lic => lic.active && Boolean((lic as any).expiring));
              const hasActive = dataTrafficLicencesArr.some(lic => lic.active);
              const isExpired = !hasActive;

              if (status === 'EXPIRING') return hasExpiring;
              if (status === 'EXPIRED') return isExpired;
              if (status === 'ACTIVE') return hasActive;
              return true;
            });
          }

          // sortModel
          const sortModel = filters.sortModel;
          if (Array.isArray(sortModel) && sortModel.length > 0) {
            const sm = sortModel[0];
            const col = sm.colId;
            const dir = sm.sort === 'asc' ? 1 : -1;
            result.sort((a, b) => {
              const va = (a[col] ?? '').toString().toLowerCase();
              const vb = (b[col] ?? '').toString().toLowerCase();
              if (va < vb) return -1 * dir;
              if (va > vb) return 1 * dir;
              return 0;
            });
          }

          return result;
        };

        const paginator = dataTrafficLicences?.paginator || { page: 1, pageSize: 10 };
        const allData = dataTrafficLicences?.allData;

        // If we already have allData in the store, apply filters and paginate locally without calling the API
        if (Array.isArray(allData) && allData.length > 0) {
          const filtered = applyClientFilters(allData);
          const total = filtered.length;
          const start = (paginator.page - 1) * paginator.pageSize;
          const end = start + paginator.pageSize;
          const pageItems = filtered.slice(start, end);

          return from([
            fromActions.setDataTrafficLicencesList({ response: pageItems, totalNumber: total }),
            fromActions.setDataTrafficLicencesLoading({ isLoading: false }),
          ]);
        }

        // Otherwise fetch full list once and store it, then apply filters and paginate
        return this.licenceService.getLicences().pipe(
          switchMap((vehiclesData) => {
            const all = Array.isArray(vehiclesData) ? vehiclesData : [];
            const filtered = applyClientFilters(all);
            const total = filtered.length;
            const start = (paginator.page - 1) * paginator.pageSize;
            const end = start + paginator.pageSize;
            const pageItems = filtered.slice(start, end);

            return from([
              fromActions.setDataTrafficLicencesAllData({ allData: all }),
              fromActions.setDataTrafficLicencesList({ response: pageItems, totalNumber: total }),
              fromActions.setDataTrafficLicencesLoading({ isLoading: false }),
            ]);
          }),
          catchError(() => from([
            fromActions.setDataTrafficLicencesList({ response: [], totalNumber: 0 }),
            fromActions.setDataTrafficLicencesLoading({ isLoading: false }),
          ])),
        );
      }),
    ),
  );

  getStoreOrderLicencesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getStoreOrderLicencesList,
        fromActions.setStoreOrderLicencesPaginator),
      withLatestFrom(
        this.store.select(selectStoreOrderLicences),
      ),
      tap(() => this.store.dispatch(fromActions.setStoreOrderLicencesLoading({ isLoading: true }))),
      switchMap(([_, storeOrderLicences]) => {
        // Helper to apply search, checkbox and filterModel and sort to a dataset
        const applyClientFilters = (data: any[] = []) => {
          const filters = storeOrderLicences?.filters || {};

          let result = Array.isArray(data) ? data.slice() : [];

          // qSearch: apply to vin and ctmSerialNumber
          const q = (filters.qSearch || '').toString().trim().toLowerCase();
          if (q) {
            result = result.filter(item => {
              const vin = (item.vin || '').toString().toLowerCase();
              const ctm = (item.ctmSerialNumber || '').toString().toLowerCase();
              const customerName = (item.customerName || '').toString().toLowerCase();
              return vin.includes(q) || ctm.includes(q) || customerName.includes(q);
            });
          }

          // checkboxFilters: active, expiring, expired
          const cb = filters.checkboxFilters;
          if (cb && (cb.active || cb.expiring || cb.expired)) {
            result = result.filter(item => {
              const storeOrderLicencesArr = Array.isArray(item.licences) ? item.licences : [];
              const hasExpiring = storeOrderLicencesArr.some(lic => lic.active && Boolean((lic as any).expiring));
              const hasActive = storeOrderLicencesArr.some(lic => lic.active);
              const isExpired = !hasActive;

              let keep = false;
              if (cb.expiring && hasExpiring) keep = true;
              if (cb.active && hasActive) keep = true;
              if (cb.expired && isExpired) keep = true;

              return keep;
            });
          }

          // filterModel (e.g. licenceStatus equals some value)
          const filterModel = filters.filterModel;
          if (filterModel && filterModel.licenceStatus && filterModel.licenceStatus.filter) {
            const status = filterModel.licenceStatus.filter;
            result = result.filter(item => {
              const storeOrderLicencesArr = Array.isArray(item.licences) ? item.licences : [];
              const hasExpiring = storeOrderLicencesArr.some(lic => lic.active && Boolean((lic as any).expiring));
              const hasActive = storeOrderLicencesArr.some(lic => lic.active);
              const isExpired = !hasActive;

              if (status === 'EXPIRING') return hasExpiring;
              if (status === 'EXPIRED') return isExpired;
              if (status === 'ACTIVE') return hasActive;
              return true;
            });
          }

          const productCode = (filters.productCode || '').toString().trim();
          if (productCode && productCode !== 'ALL') {
            result = result.filter(item => {
              const storeOrderLicencesArr = Array.isArray(item.licences) ? item.licences : [];
              return storeOrderLicencesArr.some(lic => lic.productCode === productCode);
            });
          }

          // sortModel
          const sortModel = filters.sortModel;
          if (Array.isArray(sortModel) && sortModel.length > 0) {
            const sm = sortModel[0];
            const col = sm.colId;
            const dir = sm.sort === 'asc' ? 1 : -1;
            result.sort((a, b) => {
              const va = (a[col] ?? '').toString().toLowerCase();
              const vb = (b[col] ?? '').toString().toLowerCase();
              if (va < vb) return -1 * dir;
              if (va > vb) return 1 * dir;
              return 0;
            });
          }

          return result;
        };

        const paginator = storeOrderLicences?.paginator || { page: 1, pageSize: 10 };
        const allData = storeOrderLicences?.allData;

        // If we already have allData in the store, apply filters and paginate locally without calling the API
        if (Array.isArray(allData) && allData.length > 0) {
          const filtered = applyClientFilters(allData);
          const total = filtered.length;
          const start = (paginator.page - 1) * paginator.pageSize;
          const end = start + paginator.pageSize;
          const pageItems = filtered.slice(start, end);

          return from([
            fromActions.setStoreOrderLicencesList({ response: pageItems, totalNumber: total }),
            fromActions.setStoreOrderLicencesLoading({ isLoading: false }),
          ]);
        }

        // Otherwise fetch full list once and store it, then apply filters and paginate
        return this.licenceService.getLicencesFromStoreOrder().pipe(
          switchMap((vehiclesData) => {
            const all = Array.isArray(vehiclesData) ? vehiclesData : [];
            const filtered = applyClientFilters(all);
            const total = filtered.length;
            const start = (paginator.page - 1) * paginator.pageSize;
            const end = start + paginator.pageSize;
            const pageItems = filtered.slice(start, end);

            return from([
              fromActions.setStoreOrderLicencesAllData({ allData: all }),
              fromActions.setStoreOrderLicencesList({ response: pageItems, totalNumber: total }),
              fromActions.setStoreOrderLicencesLoading({ isLoading: false }),
            ]);
          }),
          catchError(() => from([
            fromActions.setStoreOrderLicencesList({ response: [], totalNumber: 0 }),
            fromActions.setStoreOrderLicencesLoading({ isLoading: false }),
          ])),
        );
      }),
    ),
  );

  getSelectedDataTrafficLicenceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getSelectedDataTrafficLicenceList,
        fromActions.setSelectedDataTrafficLicencePaginator),
      withLatestFrom(
        this.store.select(selectSelectedDataTrafficLicence),
      ),
      tap(() => this.store.dispatch(fromActions.setSelectedDataTrafficLicenceLoading({ isLoading: true }))),
      switchMap(([_, selectedLicence]) =>
        this.licenceService.getLicence(selectedLicence.vin).pipe(
          switchMap((vehiclesData) => from([
            fromActions.setSelectedDataTrafficLicenceList({ response: vehiclesData }),
            fromActions.setSelectedDataTrafficLicenceLoading({ isLoading: false }),
          ])),
          catchError(() => from([
            fromActions.setSelectedDataTrafficLicenceList({ response: [] }),
            fromActions.setSelectedDataTrafficLicenceLoading({ isLoading: false }),
          ])),
        )),
    ),
  );

  getLicenceProductCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getLicenceProductCodes),
      tap(() => this.store.dispatch(fromActions.setLicenceProductCodesLoading({ isLoading: true }))),
      switchMap(() =>
        this.licenceService.getLicenceProductCodes().pipe(
          switchMap((productCodes) => from([
            fromActions.setLicenceProductCodesList({ productCodes: Array.isArray(productCodes) ? productCodes : [] }),
            fromActions.setLicenceProductCodesLoading({ isLoading: false }),
          ])),
          catchError(() => from([
            fromActions.setLicenceProductCodesList({ productCodes: [] }),
            fromActions.setLicenceProductCodesLoading({ isLoading: false }),
          ])),
        )),
    ),
  );

  getSelectedStoreOrderLicenceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getSelectedStoreOrderLicenceList,
        fromActions.setSelectedStoreOrderLicencePaginator),
      withLatestFrom(
        this.store.select(selectSelectedStoreOrderLicence),
      ),
      tap(() => this.store.dispatch(fromActions.setSelectedStoreOrderLicenceLoading({ isLoading: true }))),
      switchMap(([_, selectedLicence]) =>
        this.licenceService.getLicencesFromStoreOrderByVin(selectedLicence.vin).pipe(
          switchMap((vehiclesData) => from([
            fromActions.setSelectedStoreOrderLicenceList({ response: vehiclesData }),
            fromActions.setSelectedStoreOrderLicenceLoading({ isLoading: false }),
          ])),
          catchError(() => from([
            fromActions.setSelectedStoreOrderLicenceList({ response: {} }),
            fromActions.setSelectedStoreOrderLicenceLoading({ isLoading: false }),
          ])),
        )),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private licenceService: LicenceService,
  ) {
    this.store.select(selectUser).subscribe(user => {
      if (!user?.user_metadata) {
        return;
      }
      this.language = user?.user_metadata?.language || 'en';
    });
  }
}
